import { api, ApiError } from './api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5202/api';

export interface MorningPageDto {
  id: string;
  userId: string;
  date: string;
  photoUrl: string;
  photoUrls: string[];
  photoCount: number;
  thumbnailUrl?: string;
  ocrText?: string;
  analysisJson?: string;
  isAnalyzed: boolean;
  hasOcrText: boolean;
  hasAnalysis: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface MorningPageDetailDto extends MorningPageDto {
  insights: MorningPageInsightDto[];
}

export interface MorningPageInsightDto {
  id: string;
  morningPageId: string;
  userId: string;
  insightType: InsightType;
  title: string;
  content: string;
  confidence: number;
  relatedProjectId?: string;
  relatedProjectName?: string;
  isActionable: boolean;
  createdAt: string;
}

export type InsightType = 'Theme' | 'Emotion' | 'Pattern' | 'SelfSabotage' | 'Recommendation';

export interface WeeklyInsightSummaryDto {
  id: string;
  userId: string;
  weekStartDate: string;
  weekEndDate: string;
  weekNumber: number;
  topThemes: string[];
  emotionalTrend: string;
  keyInsights: string;
  recommendations: string;
  correlationInsights?: string;
  morningPagesCount: number;
  generatedAt: string;
  isRead: boolean;
}

export interface UploadMorningPageRequest {
  photo: File;
  date?: string;
}

export interface UpdateMorningPageRequest {
  photoUrl?: string;
  ocrText?: string;
  analysisJson?: string;
}

export interface UpdateOcrTextRequest {
  ocrText: string;
  triggerReanalysis?: boolean;
}

export interface GenerateWeeklySummaryRequest {
  weekStartDate: string;
  forceRegenerate?: boolean;
}

export interface MorningPageListResponse {
  items: MorningPageDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export const morningPagesService = {
  /**
   * Get today's morning page if it exists.
   */
  async getTodayMorningPage(): Promise<MorningPageDto | null> {
    try {
      return await api.get<MorningPageDto>('/morningpages/today');
    } catch {
      return null;
    }
  },

  /**
   * Get a morning page by ID.
   */
  async getMorningPageById(id: string): Promise<MorningPageDto | null> {
    try {
      return await api.get<MorningPageDto>(`/morningpages/${id}`);
    } catch {
      return null;
    }
  },

  /**
   * Get paginated list of user's morning pages.
   */
  async getMorningPages(
    pageNumber = 1,
    pageSize = 10,
    startDate?: string,
    endDate?: string
  ): Promise<MorningPageListResponse> {
    const params = new URLSearchParams();
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const items = await api.get<MorningPageDto[]>(`/morningpages?${params.toString()}`);
    return {
      items,
      totalCount: items.length,
      pageNumber,
      pageSize,
    };
  },

  /**
   * Upload a new morning page photo.
   */
  async uploadMorningPage(request: UploadMorningPageRequest): Promise<MorningPageDto> {
    const formData = new FormData();
    formData.append('photo', request.photo);
    if (request.date) {
      formData.append('date', request.date);
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}/morningpages/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new ApiError(response.status, errorData);
    }

    return response.json();
  },

  /**
   * Update an existing morning page.
   */
  async updateMorningPage(id: string, request: UpdateMorningPageRequest): Promise<MorningPageDto> {
    return api.put<MorningPageDto>(`/morningpages/${id}`, request);
  },

  /**
   * Delete a morning page.
   */
  async deleteMorningPage(id: string): Promise<void> {
    await api.delete(`/morningpages/${id}`);
  },

  /**
   * Compress an image before upload (client-side).
   */
  async compressImage(file: File, maxWidth = 2048, quality = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        // Scale down if too large
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  },

  /**
   * Validate file type and size.
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const maxSize = 20 * 1024 * 1024; // 20MB

    if (!validTypes.includes(file.type)) {
      return { valid: false, error: 'Invalid file type. Please upload a JPEG, PNG, or WEBP image.' };
    }

    if (file.size > maxSize) {
      return { valid: false, error: 'File is too large. Maximum size is 20MB.' };
    }

    return { valid: true };
  },

  // ==================== AI Analysis Methods (Premium Feature) ====================

  /**
   * Process OCR for a morning page (Premium).
   */
  async processOcr(id: string, forceReprocess = false): Promise<MorningPageDto> {
    const params = forceReprocess ? '?forceReprocess=true' : '';
    return api.post<MorningPageDto>(`/morningpages/${id}/ocr${params}`, {});
  },

  /**
   * Update/edit OCR text for a morning page.
   */
  async updateOcrText(id: string, request: UpdateOcrTextRequest): Promise<MorningPageDto> {
    return api.put<MorningPageDto>(`/morningpages/${id}/ocr`, request);
  },

  /**
   * Trigger AI analysis for a morning page (Premium).
   */
  async analyze(id: string, includeAppData = true): Promise<MorningPageDetailDto> {
    const params = includeAppData ? '?includeAppData=true' : '?includeAppData=false';
    return api.post<MorningPageDetailDto>(`/morningpages/${id}/analyze${params}`, {});
  },

  /**
   * Get insights for a specific morning page.
   */
  async getInsights(morningPageId: string): Promise<MorningPageInsightDto[]> {
    return api.get<MorningPageInsightDto[]>(`/morningpages/${morningPageId}/insights`);
  },

  /**
   * Get all insights for the current user with optional filters.
   */
  async getAllInsights(options?: {
    insightType?: InsightType;
    actionableOnly?: boolean;
    fromDate?: string;
    toDate?: string;
  }): Promise<MorningPageInsightDto[]> {
    const params = new URLSearchParams();
    if (options?.insightType) params.append('insightType', options.insightType);
    if (options?.actionableOnly) params.append('actionableOnly', 'true');
    if (options?.fromDate) params.append('fromDate', options.fromDate);
    if (options?.toDate) params.append('toDate', options.toDate);

    const queryString = params.toString();
    return api.get<MorningPageInsightDto[]>(`/morningpages/insights${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get the latest insights for the current user.
   */
  async getLatestInsights(count = 10): Promise<MorningPageInsightDto[]> {
    return api.get<MorningPageInsightDto[]>(`/morningpages/insights/latest?count=${count}`);
  },

  // ==================== Weekly Summary Methods ====================

  /**
   * Get the latest weekly insight summary (Premium).
   */
  async getLatestWeeklySummary(): Promise<WeeklyInsightSummaryDto | null> {
    try {
      return await api.get<WeeklyInsightSummaryDto>('/morningpages/insights/weekly');
    } catch {
      return null;
    }
  },

  /**
   * Get a specific week's insight summary (Premium).
   */
  async getWeeklySummary(weekStart: string): Promise<WeeklyInsightSummaryDto | null> {
    try {
      return await api.get<WeeklyInsightSummaryDto>(`/morningpages/insights/weekly/${weekStart}`);
    } catch {
      return null;
    }
  },

  /**
   * Generate/regenerate weekly summary (Premium).
   */
  async generateWeeklySummary(request: GenerateWeeklySummaryRequest): Promise<WeeklyInsightSummaryDto> {
    return api.post<WeeklyInsightSummaryDto>('/morningpages/insights/weekly/generate', request);
  },

  /**
   * Mark a weekly summary as read.
   */
  async markWeeklySummaryRead(id: string): Promise<void> {
    await api.put(`/morningpages/insights/weekly/${id}/read`, {});
  },
};
